/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import type { RefinementProps } from '../types';

/**
 * THE TYPE-AHEAD APPEARS ONLY ONCE THE LIST CANNOT BE SEEN AT ONCE, and this
 * number is derived from two values that were already measured and shipped
 * rather than picked.
 *
 * The value list is capped and scrolls at `--facet-value-list-max-height` —
 * 300px on NM, 350px on BG. Measured on the running app at 1280 with the
 * Designer group expanded, the rendered rows come out at:
 *
 *   NM PLP womens-clothing   19 values   scrollHeight 748   ~39.4px per row
 *   BG PLP womens-clothing   20 values   scrollHeight 732   ~36.6px per row
 *
 * So NM's 300px window holds ~7.6 rows and BG's 350px window holds ~9.6. Ten
 * is the first count at which the list overflows its cap on BOTH brands — i.e.
 * the first count at which a shopper genuinely cannot see every designer
 * without scrolling, which is the condition the control exists to relieve.
 *
 * Below it the input is not rendered at all. That is deliberate: live BG
 * carries this input over 270 designers, where it is essential. Reproducing
 * the artifact over a six-value list would be reproducing live's markup while
 * inverting live's intent, and a control that solves a problem the surface
 * does not have reads as chrome.
 *
 * The gate degrades in the safe direction. It is right when the catalogue is
 * large, and it removes itself rather than misleading when the catalogue is
 * small — so as designer coverage grows the affordance appears on its own with
 * no code change.
 */
const SEARCH_INPUT_MIN_VALUES = 10;

type DefaultRefinementProps = RefinementProps & {
    /**
     * Renders the type-ahead above the value list, subject to the length gate
     * above. Set by the dispatcher for the Designer group only — this is the
     * fallback renderer for several groups (Availability, Sale, Gender, and
     * BG's Color), and none of those is long enough or a live source for it.
     */
    searchable?: boolean;
};

export default function DefaultRefinement({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
    searchable = false,
}: DefaultRefinementProps): ReactElement {
    const { t } = useTranslation('categoryRefinements');
    const [query, setQuery] = useState('');

    const showSearch = searchable && values.length >= SEARCH_INPUT_MIN_VALUES;

    /* The ORIGINAL index is carried through the filter so the generated
       checkbox id for a given value does not move as the query narrows the
       list. Filtering by position would renumber every row on each keystroke,
       which breaks the label/control association a screen reader has already
       announced. */
    const visible = useMemo(() => {
        const indexed = values.map((value, index) => ({ value, index }));
        if (!showSearch) return indexed;
        const needle = query.trim().toLowerCase();
        if (!needle) return indexed;
        return indexed.filter(({ value }) =>
            String(value.label || value.value)
                .toLowerCase()
                .includes(needle)
        );
    }, [values, query, showSearch]);

    return (
        <div>
            {showSearch && (
                /* THE INPUT SITS AT THE TOP OF THE EXPANDED GROUP, above the
                   value list. Live BG renders it as the first child of the
                   Designer tabpanel's section, with the value list as the
                   next sibling — `bg-search-facet-d-1024` at 1024, the
                   Designer tab bounded by its own `role="tab"` header and the
                   next one, with the input immediately before the `dl` of
                   options.

                   The placeholder and the accessible name are the same string
                   on live, both reading `Search Designer`. That copy already
                   existed in the en-US resource and is used verbatim rather
                   than invented. It is measured on BG only — NM ships no
                   capture of this control, so NM's copy is owner-reported
                   rather than capture-backed.

                   `defaultValue` is what makes the key legal: the key union is
                   augmented from the en-GB resource, and this key is en-US
                   only, so the bare overload would not typecheck. Same
                   mechanism as the cart gift toggle. */
                <Input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    /* Enter must not submit or navigate. The rail is not
                       inside a form today, but a bare `type="search"` input
                       implicit-submits if one is ever wrapped around it, and
                       that would reload the results the shopper is filtering
                       against. */
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') event.preventDefault();
                    }}
                    aria-label={t('searchDesigner', { defaultValue: 'Search Designer' })}
                    placeholder={t('searchDesigner', { defaultValue: 'Search Designer' })}
                    data-testid={`refinement-search-${attributeId}`}
                    className="mt-2"
                />
            )}
            {/* THE VALUE LIST SCROLLS INSIDE ITS GROUP once it passes the
               brand's cap. Live does this on both brands — NM's served
               stylesheet declares a 300px max-height with `overflow:auto` on
               its Designer filter, and BG's captures render a 350px cap over
               `overflow-y-scroll` on the expanded Designer list. Full evidence
               and the numbers sit on --facet-value-list-max-height in the two
               brand token files.

               THE CAP IS HERE, NOT ON THE RADIX CONTENT DIV ABOVE, on purpose:
               the content div carries `data-state` plus an inline height that
               drives the open/close animation, so a max-height there fights the
               animation rather than bounding the list. It is also NOT a blanket
               rule over every group — the price RANGE control and the colour
               swatch grid render through their own components and stay
               uncapped, which is what live does too.

               The fallback keeps the utility inert for a brand that declares
               neither name: `max-height: none` is what an uncapped list already
               has, so an unset brand cannot move. */}
            <div className="space-y-1 mt-2 max-h-[var(--facet-value-list-max-height,none)] overflow-y-auto">
                {visible.map(({ value, index }) => {
                    const id = `refine-${attributeId}-${index}`;
                    const isSelected = isFilterSelected(attributeId, value.value);

                    return (
                        <label
                            key={`${attributeId}:${value.value}`}
                            htmlFor={id}
                            className="flex items-center p-2 hover:bg-muted/30 cursor-pointer">
                            <Checkbox
                                id={id}
                                checked={isSelected}
                                onCheckedChange={() => toggleFilter(attributeId, value.value)}
                                className="size-4"
                            />
                            {/* THE PER-VALUE COUNT IS PART OF THE LABEL LINE, not a
                                badge at the trailing edge. Every live source that
                                shows one draws it as parenthesised text sharing the
                                label's own type, on both brands:

                                  BG search rail, Designer — one span carrying
                                  `A.L.C.  ( 73 )` at 13px over 18px leading, 526
                                  raw matches over the doubled emission (= 263
                                  rows), every one on the identical class, in
                                  bg-search-d-1280 and again in bg-search-facet-d-1024
                                  and bg-search-designers-375.

                                  BG mobile filter drawer, Color — one span carrying
                                  `Black  (779)` at 12px over 20px leading,
                                  bg-search-color-375 line 146.

                                  NM search rail, Category — the count is a nested
                                  span with `pl-[5px]` reading `( 8890 )` directly
                                  after the label, search-d-1280 line 432. That is
                                  the 5px lead used below, and it is the only lead
                                  any live source states.

                                Nothing on either brand, at any width, on any
                                surface, carries a filled rounded chip here. The
                                count nests INSIDE the label span so it inherits the
                                row's type — which is what BG does literally, both
                                halves in one element — and so it wraps with the
                                label rather than floating away from it.

                                NO COLOUR IS SET, deliberately. BG leaves the count
                                inheriting the row colour. NM sets rgb(92,92,92) but
                                only on its CATEGORY count; NM ships no count at all
                                on Designer, Size, Color or Price, so there is no NM
                                source for THIS element and any value here would be
                                invented. Inheriting is the neutral that matches the
                                one brand that does render it. */}
                            <span className="type-label-medium ml-3">
                                {value.label || value.value}
                                {value.hitCount !== undefined && (
                                    <span data-testid="refinement-value-count" className="pl-[5px]">
                                        ({value.hitCount})
                                    </span>
                                )}
                            </span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}
